import React from "react";
import {
    Box,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import DataTable, { TableColumn, ExpanderComponentProps } from "react-data-table-component";

import {
    IoCubeOutline,
    IoArrowDownOutline,
    IoArrowUpOutline,
} from "react-icons/io5";

import { IMovimientoInventario } from "../../../../interfaces/pos.interface";

interface Props {
    movimientos: IMovimientoInventario[];
}

// =====================================================
// ESTADO SIN DATOS
// =====================================================

const TablaSinDatos = () => (
    <Box className="flex flex-col items-center justify-center py-16">
        <IoCubeOutline size={48} className="mb-3 text-slate-300" />

        <Typography variant="h6" className="text-slate-500">
            No hay movimientos de inventario
        </Typography>

        <Typography variant="body2" className="mt-1 text-slate-400">
            No se encontraron movimientos para los filtros seleccionados.
        </Typography>
    </Box>
);

// =====================================================
// FUNCIONES AUXILIARES
// =====================================================

const esEntrada = (tipo: string) =>
    tipo.toLowerCase().includes("entrada");

const formatearFecha = (fecha: string) => {
    if (!fecha) return "-";

    const date = new Date(fecha);

    if (isNaN(date.getTime())) return fecha;

    return new Intl.DateTimeFormat("es-CO", {
        dateStyle: "short",
        timeStyle: "short",
    }).format(date);
};

// =====================================================
// DETALLE EXPANDIBLE
// =====================================================

const DetalleMovimiento = ({
    data: movimiento,
}: ExpanderComponentProps<IMovimientoInventario>) => {
    const entrada = esEntrada(movimiento.tipo_operacion);

    return (
        <Box className="bg-slate-50 px-6 py-4 md:px-8">
            <Typography
                variant="subtitle2"
                className="mb-3 font-bold text-slate-700"
            >
                Detalle de productos
            </Typography>

            <Box className="overflow-x-auto rounded-md border border-slate-200 bg-white">
                <Table size="small">
                    <TableHead>
                        <TableRow className="bg-slate-50">
                            <TableCell>
                                <strong>Código</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Descripción</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Talla</strong>
                            </TableCell>
                            <TableCell align="center">
                                <strong>Cantidad</strong>
                            </TableCell>
                            <TableCell align="center">
                                <strong>Nuevo stock</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {movimiento.productos.map((producto) => (
                            <TableRow key={producto.id} hover>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        className="font-mono text-xs text-slate-600"
                                    >
                                        {producto.codigo}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        className="text-slate-700"
                                    >
                                        {producto.descripcion}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        className="text-slate-700"
                                    >
                                        {producto.talla}
                                    </Typography>
                                </TableCell>

                                <TableCell align="center">
                                    <Chip
                                        label={producto.cantidad}
                                        size="small"
                                        color={entrada ? "success" : "error"}
                                        variant="outlined"
                                    />
                                </TableCell>

                                <TableCell align="center">
                                    <Typography
                                        variant="body2"
                                        className="font-semibold text-slate-700"
                                    >
                                        {producto.stock_actual}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Box>
    );
};

// =====================================================
// TABLA PRINCIPAL
// =====================================================

export const TableLogInventarios = ({ movimientos }: Props) => {
    const columnas: TableColumn<IMovimientoInventario>[] = [
        {
            name: "#",
            width: "60px",
            center: true,
            cell: (_row, index) => index + 1,
        },
        {
            name: "Tipo operación",
            selector: (row) => row.tipo_operacion,
            sortable: true,
            minWidth: "170px",
            cell: (row) => {
                const entrada = esEntrada(row.tipo_operacion);

                return (
                    <Chip
                        icon={
                            entrada ? (
                                <IoArrowDownOutline size={16} />
                            ) : (
                                <IoArrowUpOutline size={16} />
                            )
                        }
                        label={row.tipo_operacion}
                        size="small"
                        color={entrada ? "success" : "error"}
                        variant="outlined"
                    />
                );
            },
        },
        {
            name: "Bodega",
            selector: (row) => row.bodega || "",
            sortable: true,
            minWidth: "140px",
        },
        {
            name: "Usuario",
            selector: (row) => row.usuario || "",
            sortable: true,
            minWidth: "140px",
        },
        {
            name: "Productos",
            selector: (row) => row.productos.length,
            sortable: true,
            minWidth: "130px",
            cell: (row) => (
                <Box className="flex items-center gap-2">
                    <Box className="flex items-center rounded-full bg-slate-100 px-2 py-1">
                        <IoCubeOutline
                            size={15}
                            className="mr-1 text-slate-500"
                        />
                        <Typography
                            variant="caption"
                            className="font-semibold text-slate-600"
                        >
                            {row.productos.length}
                        </Typography>
                    </Box>

                    <Typography variant="body2" className="text-slate-600">
                        {row.productos.length === 1 ? "producto" : "productos"}
                    </Typography>
                </Box>
            ),
        },
        {
            name: "Comentario",
            selector: (row) => row.comentario || "",
            sortable: true,
            minWidth: "200px",
            wrap: true,
            cell: (row) => (
                <Typography
                    variant="body2"
                    className="py-2 text-slate-600"
                    title={row.comentario || ""}
                >
                    {row.comentario || "-"}
                </Typography>
            ),
        },
        {
            name: "Fecha",
            selector: (row) => row.fecha || "",
            sortable: true,
            minWidth: "160px",
            cell: (row) => (
                <Typography
                    variant="body2"
                    className="whitespace-nowrap text-slate-600"
                >
                    {formatearFecha(row.fecha)}
                </Typography>
            ),
        },
    ];

    return (
        <Paper elevation={0} className="overflow-hidden rounded-lg border border-slate-200">
            <DataTable
                columns={columnas}
                data={movimientos}
                pagination
                expandableRows
                expandableRowsComponent={DetalleMovimiento}
                noDataComponent={<TablaSinDatos />}
            />
        </Paper>
    );
};
